<template>
  <div class="close-claims-wrapper">
    <el-button
        size="mini"
        type="primary"
        icon="el-icon-position"
        class="custom-action-btn"
        :loading="isFetchingCount"
        @click="openModal"
    >
      Close Approved Claims
    </el-button>

    <el-dialog
        title="Close Claims"
        :visible.sync="isModalOpen"
        width="450px"
        :close-on-click-modal="false"
        :before-close="handleBeforeClose"
        custom-class="closed-claims-dialog"
    >
      <div class="confirmation-container">
        <i :class="claimsToCloseCount > 0 ? 'el-icon-warning warning-icon' : 'el-icon-info info-icon'"></i>

        <div class="message-content">
          <template v-if="claimsToCloseCount > 0 && claimsToCloseCount <= 5">
            <p>Close <strong>{{ claimsToCloseCount }}</strong> claims and generate adjustment invoices.</p>
            <p class="question-text">Proceed?</p>
          </template>

          <template v-else-if="claimsToCloseCount > 5">
            <p>You are closing <strong>{{ claimsToCloseCount }}</strong> claims.</p>
            <p>Due to the volume, this process will run in the <strong>background</strong>. You don't need to wait for the response.</p>
            <p class="question-text">Proceed?</p>
          </template>

          <template v-else>
            <p>There are no approved claims to close at this time.</p>
          </template>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
    <template v-if="claimsToCloseCount > 0">
      <el-button size="small" @click="closeModal" :disabled="isSubmitting">Cancel</el-button>
      <el-button size="small" type="primary" :loading="isSubmitting" @click="confirmClose">Accept</el-button>
    </template>
    <template v-else>
      <el-button size="small" type="primary" @click="closeModal">Close</el-button>
    </template>
  </span>
    </el-dialog>
  </div>
</template>

<script>
import constants from '@/utils/constants';
import { CASES_AND_CLAIMS_CLOSE_MULTI, CASES_AND_CLAIMS_COUNT_TO_CLOSE, CASES_AND_CLAIMS_LIST_SEARCH } from '@/store/actions';

const BACKGROUND_PROCESS_THRESHOLD = 5;

export default {
  name: 'CloseClaims',
  data() {
    return {
      isModalOpen: false,
      claimsToCloseCount: 0,
      isFetchingCount: false,
      isSubmitting: false
    };
  },
  methods: {
    async openModal() {
      this.isFetchingCount = true;
      try {
        await this.fetchClaimsCount();
        this.isModalOpen = true;
      } catch (error) {
        this.$message.error('Error fetching count.');
        this.isModalOpen = false;
      } finally {
        this.isFetchingCount = false;
      }
    },

    closeModal() {
      if (!this.isSubmitting) this.isModalOpen = false;
    },

    handleBeforeClose(done) {
      if (!this.isSubmitting) done();
    },

    async fetchClaimsCount() {
      const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
      return this.$store.dispatch(CASES_AND_CLAIMS_COUNT_TO_CLOSE)
        .then((response) => {
          this.claimsToCloseCount = response;
          loading.close();
        })
        .catch(() => {
          loading.close();
        });
    },

    async confirmClose() {
      // Logic for background process (More than 5 cases)
      if (this.claimsToCloseCount > BACKGROUND_PROCESS_THRESHOLD) {
        this.isModalOpen = false;
        this.$message.info('The process has started in the background.');

        // Execute the call without 'await' to not block the UI or show the global loading
        this.processClaims(true);
        return;
      }

      // Logic for standard process (5 cases or less)
      this.isSubmitting = true;
      try {
        await this.processClaims(false);
        this.isModalOpen = false;
      } catch (error) {
        this.$message.error('An error occurred.');
      } finally {
        this.isSubmitting = false;
      }
    },

    /**
     * @param {Boolean} isBackground - If true, it won't trigger the global UI loading overlay
     */
    async processClaims(isBackground = false) {
      let loading = null;

      if (!isBackground) {
        loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
      }

      return this.$store.dispatch(CASES_AND_CLAIMS_CLOSE_MULTI)
        .then((response) => {
          const errors = response;
          if (Object.keys(errors).length === 0) {
            this.$message.success('Claims successfully closed.');
          } else {
            this.showErrorsNotification(errors);
          }
          if (loading) loading.close();
          this.$store.dispatch(CASES_AND_CLAIMS_LIST_SEARCH);
        })
        .catch(() => {
          this.$message.error('An error occurred while processing claims.');
          if (loading) loading.close();
        });
    },

    showErrorsNotification(errors) {
      const errorsCount = Object.keys(errors).length;
      let introText = '';

      if (this.claimsToCloseCount === errorsCount) {
        introText = 'All claims failed to close. Please review the following errors:';
      } else {
        introText = 'Some claims were closed, but there were errors with the following:';
      }

      let message = `${introText} <br><ul>`;

      for (const [caseNumber, errorMsg] of Object.entries(errors)) {
        message += `<li><b>Case #${caseNumber}:</b> ${errorMsg}</li>`;
      }
      message += '</ul>';

      this.$alert(message, 'Errors summary', {
        dangerouslyUseHTMLString: true,
        type: 'warning',
        customClass: 'wide-error-modal'
      });
    }
  }
};
</script>

<style scoped>
/* Container alignment */
.confirmation-container {
  display: flex;
  align-items: flex-start;
  padding: 10px 0;
}

/* Icon styling */
.warning-icon, .info-icon {
  font-size: 28px;
  margin-right: 16px;
  margin-top: 2px; /* Fine-tune vertical alignment with first line */
}

.warning-icon { color: #e6a23c; }
.info-icon { color: #909399; }

/* Text block styling */
.message-content {
  flex: 1;
  text-align: left; /* Force left alignment */
}

.message-content p {
  margin: 0 0 8px 0;
  font-size: 14px;
  line-height: 1.5;
  color: #606266;
}

/* Question emphasis */
.question-text {
  margin-top: 12px !important;
  font-weight: 600;
  color: #303133 !important;
}

/* Dialog Footer adjustments */
.dialog-footer {
  padding-top: 10px;
}
</style>

<style>
/* Global styles for the error modal */
.wide-error-modal {
  width: 45% !important;
  max-width: 90% !important;
  max-height: 80vh;
  overflow-y: auto;
  vertical-align: top !important;
  margin-top: 80px !important;
}

.wide-error-modal .el-message-box__message {
  max-height: 400px;
  overflow-y: auto;
  padding: 10px 5px;
}
</style>
